/**
 * An equivalent copy of this file exists in billing-ui, ficon-ui and
 * lightning-ui. Unless a change is app-specific, apply it there too.
 */
import { defineStore, storeToRefs } from 'pinia';
import type { ComputedRef } from 'vue';
import { computed, ref } from 'vue';

export interface VersionStore {
  backendVersion: ComputedRef<string>;
  isHardRefreshEnabled: ComputedRef<boolean>;
  requiresHardRefresh: ComputedRef<boolean>;
  setBackendVersion: (version: string) => void;
  setHardRefreshEnabled: (isEnabled: boolean) => void;
  setRequiresHardRefresh: (isRequired: boolean) => void;
}

export const _useVersionStore = defineStore('version', (): VersionStore => {
  const _backendVersion = ref('');
  const _isHardRefreshEnabled = ref(false);
  const _requiresHardRefresh = ref(false);

  const backendVersion = computed(() => _backendVersion.value);
  const isHardRefreshEnabled = computed(() => _isHardRefreshEnabled.value);
  const requiresHardRefresh = computed(() => _requiresHardRefresh.value);

  const setBackendVersion = (version: string) => {
    _backendVersion.value = version;
  };

  const setHardRefreshEnabled = (isEnabled: boolean) => {
    if (isHardRefreshEnabled.value !== isEnabled) {
      console.warn(`'version-hard-refresh-kill-switch' is set to ${isEnabled}`);
      _isHardRefreshEnabled.value = isEnabled;
    }
  };

  const setRequiresHardRefresh = (isRequired: boolean) => {
    _requiresHardRefresh.value = isRequired;
  };

  return {
    backendVersion,
    isHardRefreshEnabled,
    requiresHardRefresh,
    setBackendVersion,
    setHardRefreshEnabled,
    setRequiresHardRefresh,
  };
});

export const useVersionStore = (): VersionStore => {
  const store = _useVersionStore();
  const { backendVersion, isHardRefreshEnabled, requiresHardRefresh } =
    storeToRefs(store);
  const { setBackendVersion, setHardRefreshEnabled, setRequiresHardRefresh } =
    store;

  return {
    backendVersion,
    isHardRefreshEnabled,
    requiresHardRefresh,
    setBackendVersion,
    setHardRefreshEnabled,
    setRequiresHardRefresh,
  };
};
